import { FormGroup, Input, Progress, RadioItem } from '@agro-club/agroclub-shared'
import { useAFormHandler } from 'analytics/hooks'
import { useFormik } from 'formik'
import { useNotification } from 'hooks/useNotification'
import { UserAddress } from 'modules/domain/userAddress/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import { FormRadio } from 'views/components/form/FormRadio'
import { SharedModalInputs, SharedModalTitle } from 'views/components/SharedModal'
import ASharedModal from 'views/components/SharedModal/ASharedModal'
import { AnalyticsPlaces } from 'views/pages/User/analyticsPlaces'

type Props = {
  setShowModal: (show: boolean) => void
  addressesRefetch: () => void
  userId: string
  address: UserAddress
}

type Fields = {
  title: string | null
  is_main: boolean
}

export const EditAddressModal: FC<Props> = ({ setShowModal, userId, address, addressesRefetch }) => {
  const { t } = useTranslation('address')
  const notify = useNotification()
  const { formProgress, formHandler } = useAFormHandler('editAddress', { id: userId })

  const formik = useFormik<Fields>({
    initialValues: {
      title: address.title,
      is_main: address.is_main,
    },

    onSubmit: formHandler(
      async () => {
        await apiClient.patch(endpoints.userAddress(userId, address.id), formik.values)
      },
      {
        onSuccess: async () => {
          await addressesRefetch()
          setShowModal(false)
          notify(Progress.SUCCESS, { title: t('address:updateAddressSuccess') })
        },
        onError: (error) => {
          const { errors } = error
          formik.setErrors(errors)
        },
      },
    ),
  })

  return (
    <ASharedModal id={AnalyticsPlaces.EDIT_ADDRESS_MODAL} size="medium" onClose={() => setShowModal(false)}>
      <SharedModalTitle>{t('editAddress')}</SharedModalTitle>
      <SharedModalInputs>
        <FormGroup error={formik.errors.title} label={t('addressNameLabel')}>
          <Input {...formik.getFieldProps('title')} invalid={!!formik.errors.title} />
        </FormGroup>
        <FormRadio label={t('legalType:label')} formik={formik} field="is_main">
          <RadioItem value={true} label={t('main')} />
          <RadioItem value={false} label={t('additional')} />
        </FormRadio>
      </SharedModalInputs>
      <ADefaultModalFooter
        progress={formProgress}
        confirmButtonText={t('common:save')}
        onClose={() => setShowModal(false)}
        onConfirm={() => formik.handleSubmit()}
      />
    </ASharedModal>
  )
}
